import { LoadingButton } from "@mui/lab";
import { FormHelperText, Grid, Tooltip, Typography } from "@mui/material";
import { get, pickBy } from "lodash";
import _ from "lodash/fp";
import moment from "moment";
import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import { BE_DATE_FORMAT, some } from "../constants";
import ArrayElement from "./element/array-element/ArrayElement";
import AutoCompleteElement from "./element/autocomplete/AutoCompleteElement";
import FormControlAutoComplete from "./element/autocomplete/FormControlAutoComplete";
import CheckBoxElement from "./element/checkbox/CheckBoxElement";
import DateRangePickerElement from "./element/date-range/DateRangePickerElement";
import DatePickerElement from "./element/datepicker-element/DatePickerElement";
import DateTimePickerElement from "./element/datepickerTime-element/DateTimePickerElement";
import MultipleCheckBoxElement from "./element/multiple-checkbox/MultipleCheckBoxElement";
import MultipleRadioElement from "./element/multiple-radio/MultipleRadioElement";
import RadioElement from "./element/radio/RadioElement";
import SectionElement from "./element/section-element/SectionElement";
import SelectElement from "./element/select/SelectElement";
import SwitchElement from "./element/switch/SwitchElement";
import TextEditorElement from "./element/text-editor/TextEditorElement";
import TextFieldElement from "./element/text-field/TextFieldElement";
import TimePickerElement from "./element/timepicker-element/TimePickerElement";
import { ElementFormProps, ElementType, IControllerRenderProps } from "./utils";

interface Props {
  fieldName: string;
  rawElement?: boolean;
  propsElement: ElementFormProps;
}

SchemaElement.defaultProps = {};

function SchemaElement(props: Props | some) {
  const { fieldName, rawElement, propsElement } = props;
  const {
    type,
    component,
    unregister,
    xs,
    register: registerElement = {},
    propsWrapper,
    defaultValue,
    inputType,
    tooltipError,
    submitLabel,
    typeButton,
    noHelperText,
    shouldUnregister,
    hotKeys,
    hidden,
    ...rest
  } = propsElement;

  const methods = useFormContext();
  const {
    control,
    register,
    formState: { errors },
    setValue,
  } = methods;

  const { valueAsDate, valueAsNumber, pattern, setValueAs, ...restRegis } =
    registerElement;

  const required = Object.keys(restRegis).length > 0;

  const getElement = React.useMemo(() => {
    let element;
    if (!type) {
      return [];
    }
    switch (type as ElementType) {
      case "hidden":
        element = <input type="hidden" {...register(fieldName)} />;
        break;
      case "text-field":
        element = unregister ? (
          <TextFieldElement
            fullWidth
            onChange={(e) => {
              rest?.onChange && rest?.onChange(e.target.value);
            }}
            type={inputType}
          />
        ) : (
          <Controller
            shouldUnregister={shouldUnregister}
            name={fieldName}
            control={control}
            rules={registerElement}
            defaultValue={defaultValue}
            render={({
              field: { onChange, value, name, ref },
              fieldState: { error },
            }) => (
              <>
                <TextFieldElement
                  inputRef={ref}
                  fullWidth
                  required={required}
                  name={name}
                  {...rest}
                  // value={value || ''}//In case value is 0 this will show empty string =>wrong
                  value={
                    typeof value === "string" || typeof value === "number"
                      ? value
                      : ""
                  }
                  onChange={(...params) => {
                    onChange(...params);
                    rest.onChange && rest.onChange(...params);
                  }}
                  type={inputType}
                  error={!!error}
                />
              </>
            )}
          />
        );
        break;
      case "checkbox":
        element = unregister ? (
          <CheckBoxElement {...rest} />
        ) : (
          <Controller
            shouldUnregister={shouldUnregister}
            name={fieldName}
            control={control}
            rules={registerElement}
            defaultValue={defaultValue || false}
            render={({
              field: { onChange, ...field },
              fieldState: { error },
            }) => (
              <CheckBoxElement
                required={required}
                {...rest}
                onChange={(...params) => {
                  onChange(...params);
                  rest.onChange && rest.onChange(...params);
                }}
                {...field}
                error={!!error}
              />
            )}
          />
        );
        break;
      case "multiple-checkbox":
        element = unregister ? (
          <MultipleCheckBoxElement {...rest} />
        ) : (
          <Controller
            shouldUnregister={shouldUnregister}
            name={fieldName}
            control={control}
            rules={registerElement}
            defaultValue={defaultValue || []}
            render={({
              field: { onChange, ...field },
              fieldState: { error },
            }) => (
              <MultipleCheckBoxElement
                required={required}
                {...rest}
                onChange={(...params) => {
                  onChange(...params);
                  rest.onChange && rest.onChange(...params);
                }}
                {...field}
                error={!!error}
              />
            )}
          />
        );
        break;
      case "radio":
        element = unregister ? (
          <RadioElement {...rest} />
        ) : (
          <Controller
            shouldUnregister={shouldUnregister}
            name={fieldName}
            control={control}
            rules={registerElement}
            defaultValue={defaultValue || false}
            render={({
              field: { onChange, ...field },
              fieldState: { error },
            }) => (
              <RadioElement
                required={required}
                {...rest}
                onChange={(...params) => {
                  onChange(...params);
                  rest.onChange && rest.onChange(...params);
                }}
                {...field}
                error={!!error}
              />
            )}
          />
        );
        break;
      case "multiple-radio":
        element = unregister ? (
          <MultipleRadioElement {...rest} />
        ) : (
          <Controller
            shouldUnregister={shouldUnregister}
            name={fieldName}
            control={control}
            rules={registerElement}
            defaultValue={defaultValue}
            render={({
              field: { onChange, ...field },
              fieldState: { error },
            }) => (
              <MultipleRadioElement
                required={required}
                {...rest}
                onChange={(...params) => {
                  onChange(...params);
                  rest.onChange && rest.onChange(...params);
                }}
                {...field}
                error={!!error}
              />
            )}
          />
        );
        break;
      case "select":
        element = unregister ? (
          <SelectElement {...rest} />
        ) : (
          <Controller
            shouldUnregister={shouldUnregister}
            name={fieldName}
            control={control}
            rules={registerElement}
            defaultValue={defaultValue}
            render={({
              field: { onChange, ...field },
              fieldState: { error },
            }) => (
              <SelectElement
                required={required}
                {...rest}
                onChange={(...params) => {
                  onChange(...params);
                  rest.onChange && rest.onChange(...params);
                }}
                {...field}
                error={!!error}
              />
            )}
          />
        );
        break;
      case "switch":
        element = unregister ? (
          <SwitchElement {...rest} />
        ) : (
          <Controller
            shouldUnregister={shouldUnregister}
            name={fieldName}
            control={control}
            rules={registerElement}
            defaultValue={defaultValue || false}
            render={({
              field: { onChange, ...field },
              fieldState: { error },
            }) => (
              <SwitchElement
                required={required}
                {...rest}
                disabled={rest.disabled || rest.readOnly}
                onChange={(...params) => {
                  onChange(...params);
                  rest.onChange && rest.onChange(...params);
                }}
                {...field}
                error={!!error}
              />
            )}
          />
        );
        break;
      case "auto-complete":
        element = unregister ? (
          <FormControlAutoComplete fullWidth {...rest} />
        ) : (
          <Controller
            shouldUnregister={shouldUnregister}
            name={fieldName}
            control={control}
            rules={registerElement}
            defaultValue={defaultValue}
            render={(propsRender) => (
              <AutoCompleteElement
                required={required}
                {...rest}
                {...propsRender}
              />
            )}
          />
        );
        break;
      case "datePicker":
        element = unregister ? (
          <DatePickerElement {...(rest as any)} />
        ) : (
          <Controller
            shouldUnregister={shouldUnregister}
            name={fieldName}
            control={control}
            rules={registerElement}
            defaultValue={defaultValue || null}
            render={({
              field: { onChange, ...field },
              fieldState: { error },
            }) => (
              <DatePickerElement
                required={required}
                {...rest}
                onChange={(value) => {
                  const tmp = value
                    ? moment(value).format(BE_DATE_FORMAT)
                    : null;
                  onChange(tmp);
                  rest.onChange && rest.onChange(tmp);
                }}
                {...field}
                error={!!error}
              />
            )}
          />
        );
        break;
      case "dateTimePicker":
        element = unregister ? (
          <DatePickerElement {...(rest as any)} />
        ) : (
          <Controller
            shouldUnregister={shouldUnregister}
            name={fieldName}
            control={control}
            rules={registerElement}
            defaultValue={defaultValue || null}
            render={({
              field: { onChange, ...field },
              fieldState: { error },
            }) => (
              <DateTimePickerElement
                required={required}
                {...rest}
                onChange={(value) => {
                  const tmp = value
                    ? moment(value).format(BE_DATE_FORMAT)
                    : null;
                  onChange(tmp);
                  rest.onChange && rest.onChange(tmp);
                }}
                {...field}
                error={!!error}
              />
            )}
          />
        );
        break;
      case "timePicker":
        element = unregister ? (
          <TimePickerElement {...(rest as any)} />
        ) : (
          <Controller
            shouldUnregister={shouldUnregister}
            name={fieldName}
            control={control}
            rules={registerElement}
            defaultValue={defaultValue || null}
            render={({
              field: { onChange, ...field },
              fieldState: { error },
            }) => (
              <TimePickerElement
                required={required}
                {...rest}
                onChange={(value) => {
                  onChange(value);
                  rest.onChange && rest.onChange(value);
                }}
                {...field}
                error={!!error}
              />
            )}
          />
        );
        break;
      case "dateRange":
        element = unregister ? (
          <DateRangePickerElement {...(rest as any)} />
        ) : (
          <Controller
            shouldUnregister={shouldUnregister}
            name={fieldName}
            control={control}
            rules={registerElement}
            defaultValue={defaultValue || null}
            render={({
              field: { value, onChange, ...field },
              fieldState: { error },
            }) => (
              <DateRangePickerElement
                required={required}
                {...field}
                {...rest}
                value={[value?.from || null, value?.to || null]}
                onChange={(value: any) => {
                  const tmp = {
                    from: value?.[0]
                      ? moment(value?.[0]).format(BE_DATE_FORMAT)
                      : null,
                    to: value?.[1]
                      ? moment(value?.[1]).format(BE_DATE_FORMAT)
                      : null,
                  };
                  onChange(tmp);
                  rest.onChange && rest.onChange(tmp);
                }}
                error={!!error}
              />
            )}
          />
        );
        break;
      case "text-editor":
        element = unregister ? (
          <TextEditorElement {...rest} />
        ) : (
          <Controller
            shouldUnregister={shouldUnregister}
            name={fieldName}
            control={control}
            rules={registerElement}
            defaultValue={defaultValue || ""}
            render={({
              field: { onChange, ...field },
              fieldState: { error },
            }) => (
              <TextEditorElement
                required={required}
                {...rest}
                onChange={(value) => {
                  // onChange(value);
                  setValue(fieldName, value, { shouldDirty: false });
                  rest.onChange && rest.onChange(value);
                }}
                {...field}
                error={!!error}
              />
            )}
          />
        );
        break;
      case "array":
        element = <ArrayElement {...rest} name={fieldName} />;
        break;
      case "section":
        element = <SectionElement {...rest} name={fieldName} />;
        break;
      case "submitButton":
        element = (
          <LoadingButton
            type={typeButton || "submit"}
            variant="contained"
            color="primary"
            // loadingPosition="start"
            {...rest}
          >
            {rest.label || submitLabel || <FormattedMessage id="save" />}
          </LoadingButton>
        );
        break;
      case "button":
        element = (
          <LoadingButton
            type={typeButton || "button"}
            variant="contained"
            color="primary"
            // loadingPosition="start"
            {...rest}
          >
            {rest.label}
          </LoadingButton>
        );
        break;
      case "raw":
        element = unregister ? (
          component()
        ) : (
          <Controller
            shouldUnregister={shouldUnregister}
            name={fieldName}
            control={control}
            rules={registerElement}
            defaultValue={defaultValue}
            render={component}
          />
        );
        break;
      default:
        try {
          element = unregister ? (
            typeof type === "function" ? (
              type(rest)
            ) : (
              React.createElement(type as any, rest)
            )
          ) : (
            <Controller
              shouldUnregister={shouldUnregister}
              name={fieldName}
              defaultValue={defaultValue}
              render={(propsRender) => (
                <>
                  {typeof type === "function"
                    ? type({ ...rest, ...propsRender })
                    : React.createElement(
                        type as any,
                        {
                          ...rest,
                          ...propsRender,
                        } as IControllerRenderProps
                      )}
                </>
              )}
              rules={registerElement}
            />
          );
        } catch (e) {}
        break;
    }
    return element;
  }, [
    type,
    register,
    fieldName,
    unregister,
    inputType,
    shouldUnregister,
    control,
    registerElement,
    defaultValue,
    rest,
    typeButton,
    submitLabel,
    component,
    required,
    setValue,
  ]);

  const errorMessage = React.useCallback(() => {
    const tmp = pickBy(get(errors, fieldName), (value, key) => {
      return key !== "ref";
    });

    const type_tmp = tmp?.type;
    const message = tmp?.message;
    delete tmp.type;
    delete tmp.message;
    return { ...tmp, message: message || type_tmp };
  }, [errors, fieldName]);

  // const tmp = fieldName ? watch(fieldName, undefined) : watch();

  // const previousValue = React.useRef(undefined);

  // React.useEffect(() => {
  //   if (
  //     !isEqual(tmp || null, previousValue.current || null) &&
  //     onChangeField &&
  //     typeof previousValue.current !== 'undefined'
  //   ) {
  //     onChangeField(tmp);
  //   }
  //   previousValue.current = tmp || null;
  // }, [fieldName, onChangeField, tmp, watch]);

  const content = React.useMemo(() => {
    const messageError = rest?.readOnly ? (
      <FormattedMessage id="readOnly" />
    ) : (
      errorMessage() &&
      _.entries(errorMessage()).map(
        ([type_error, message]: [string, unknown]) =>
          typeof message === "string" &&
          (message ? type_error !== "type" : true) && (
            <span key={type_error}>
              {message === "required" ? (
                <FormattedMessage id="required" />
              ) : (
                message
              )}
              <br />
            </span>
          )
      )
    );
    return (
      <>
        {getElement}
        {type !== "submitButton" &&
          type !== "button" &&
          typeof type === "string" &&
          type !== "hidden" &&
          !noHelperText && (
            <Tooltip title={tooltipError ? messageError : ""} arrow>
              <FormHelperText
                style={{ height: 8 }}
                error={!rest?.readOnly}
                component="div"
              >
                <Typography
                  variant="caption"
                  color="inherit"
                  noWrap
                  component="div"
                >
                  {messageError}
                </Typography>
              </FormHelperText>
            </Tooltip>
          )}
      </>
    );
  }, [
    errorMessage,
    getElement,
    noHelperText,
    rest?.readOnly,
    tooltipError,
    type,
  ]);
  if (hidden) {
    return null;
  }
  if (type === "hidden") {
    return content;
  }
  if (rawElement) {
    return <div {...propsWrapper}>{content}</div>;
  }
  return (
    <Grid item xs={xs || 12} {...propsWrapper}>
      {content}
    </Grid>
  );
}

export default SchemaElement;
